<template>
  <div id="instrument-queue-view">
    <aside class="iq-sidebar">
      <nav class="iq-sidebar__nav">
        <a
          class="iq-nav-item"
          :class="{ active: activeTab === 'RELEASE' }"
          @click="activeTab = 'RELEASE'"
        >
          <span class="iq-nav-item__dot"></span>
          <span>Release Reports</span>
        </a>
        <a
          class="iq-nav-item"
          :class="{ active: activeTab === 'PENDING' }"
          @click="switchToPending"
        >
          <span class="iq-nav-item__dot"></span>
          <span>Pending Tests</span>
        </a>
        <a
          class="iq-nav-item"
          :class="{ active: activeTab === 'CRITICAL' }"
          @click="switchToCritical"
        >
          <span class="iq-nav-item__dot"></span>
          <span>Critical Tests</span>
        </a>
        <a
          class="iq-nav-item"
          :class="{ active: activeTab === 'SEARCH' }"
          @click="switchToSearch"
        >
          <span class="iq-nav-item__dot"></span>
          <span>Search by Test</span>
        </a>
      </nav>
    </aside>
    <div v-show="activeTab === 'RELEASE'" class="iq-main">
    <!-- Workspace tabs: the instrument overview plus one tab per opened instrument -->
    <div class="iq-tabs" role="tablist">
      <button
        class="iq-tab"
        role="tab"
        :class="{ active: !resultsTab.instrumentId }"
        :aria-selected="!resultsTab.instrumentId"
        @click="showOverview"
      >
        <span class="iq-tab__name">All Instruments</span>
      </button>
      <button
        v-for="tab in openTabs"
        :key="tab.instrument_id"
        class="iq-tab"
        role="tab"
        :class="{ active: tab.instrument_id === resultsTab.instrumentId }"
        :aria-selected="tab.instrument_id === resultsTab.instrumentId"
        :title="tab.name"
        @click="switchInstrumentTab(tab)"
      >
        <span class="iq-tab__name">{{ tab.name }}</span>
        <span
          class="iq-tab__close"
          title="Close tab"
          @click.stop="closeInstrumentTab(tab)"
        >×</span>
      </button>
    </div>

    <template v-if="!resultsTab.instrumentId">
    <div class="iq-page-header">
      <h2>Release Reports</h2>
      <div class="iq-page-header__actions">
        <el-button size="small" :loading="dashboardLoading" @click="loadDashboard">
          Refresh
        </el-button>
      </div>
    </div>

    <div v-if="dashboardLoading" class="iq-loading">
      <el-skeleton :rows="3" animated />
    </div>

    <div v-else-if="!dashboard.length" class="iq-empty">
      No instruments available.
    </div>

    <div v-else class="iq-tiles">
      <div
        v-for="(inst, idx) in dashboard"
        :key="inst.instrument_id || idx"
        class="iq-tile"
      >
        <div class="iq-tile__top">
          <span class="iq-tile__name">{{ inst.name }}</span>
        </div>
        <div class="iq-tile__counts">
          <div class="iq-tile__count" :class="{ highlight: inst.new_count > 0 }">
            <span>New:</span>
            <strong>{{ inst.new_count || 0 }}</strong>
          </div>
          <div class="iq-tile__count" :class="{ highlight: inst.old_count > 0 }">
            <span>Old:</span>
            <strong>{{ inst.old_count || 0 }}</strong>
          </div>
        </div>
        <div class="iq-tile__footer">
          <span v-if="inst.batch_date" class="iq-tile__date">{{ formatDate(inst.batch_date) }}</span>
          <el-dropdown
            trigger="click"
            popper-class="iq-tile-dropdown"
            @command="(cmd) => openResults(cmd, inst)"
          >
            <span class="iq-tile__action-btn">Actions ▾</span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="ALL">Review All</el-dropdown-item>
                <el-dropdown-item command="NEW" :disabled="!inst.new_count">
                  Review New
                </el-dropdown-item>
                <el-dropdown-item command="OLD" :disabled="!inst.old_count">
                  Review Old
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    </template>

    <!-- Results for the instrument in the active workspace tab -->
    <div v-else class="iq-results-view">
      <div class="iq-page-header iq-page-header--results">
        <div class="iq-results-title">
          <a class="iq-crumb" @click="showOverview">Release Reports</a>
          <span class="iq-crumb-sep">/</span>
          <h2>{{ resultsTab.name }}</h2>
          <span v-if="!resultsLoading && groupedResults.length" class="iq-results-count">
            {{ groupedResults.length }} case{{ groupedResults.length === 1 ? '' : 's' }}
          </span>
        </div>
        <div class="iq-page-header__actions">
          <el-button size="small" :loading="resultsLoading" @click="loadResults">
            Refresh
          </el-button>
        </div>
      </div>

      <div class="iq-dialog-filters">
        <div class="filter-left">
          <div class="filter-group">
            <button
              v-for="opt in categoryOptions"
              :key="opt.value"
              class="filter-btn"
              :class="{ active: resultsFilter === opt.value }"
              @click="setCategory(opt.value)"
            >{{ opt.label }}</button>
          </div>
          <div class="filter-group">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              class="filter-btn"
              :class="{ active: reviewFilter === opt.value }"
              @click="setStatus(opt.value)"
            >{{ opt.label }}</button>
          </div>
        </div>
        <button class="btn-accept-all" :disabled="acceptAllLoading || !isLabTechnologist" @click="acceptAll">
          <span v-if="acceptAllLoading" class="btn-spinner"></span>
          {{ acceptAllLoading ? 'Accepting...' : 'Accept All' }}
        </button>
      </div>

      <div v-if="resultsLoading" class="iq-loading" style="padding: 30px 0;">
        <el-skeleton :rows="6" animated />
      </div>

      <template v-else>
        <div v-if="!groupedResults.length" class="iq-empty" style="padding: 30px;">
          No results found.
        </div>

        <div v-else class="report-list report-list--results">
          <div
            v-for="(group, idx) in visibleResultGroups"
            :key="group._caseDocId"
            class="report-case"
          >
            <!-- Sticky: patient header + rule + column labels -->
            <div class="report-case__sticky">
              <div class="report-case__header">
                <div class="report-case__patient">
                  <span class="rpt-label">ACC</span>
                  <span class="rpt-dot">·</span>
                  <span class="rpt-accession">{{ group.accession_id }}</span>
                  <span class="rpt-sep"></span>
                  <span class="rpt-name">{{ group.patient_name }}</span>
                  <span class="rpt-dot">·</span>
                  <span class="rpt-meta">{{ group.patient_gender === 'MALE' ? 'M' : group.patient_gender === 'FEMALE' ? 'F' : (group.patient_gender || '—') }}</span>
                  <template v-if="group.patient_dob">
                    <span class="rpt-dot">·</span>
                    <span class="rpt-meta">{{ formatDob(group.patient_dob) }}</span>
                  </template>
                </div>
                <button
                  class="rpt-accept-btn"
                  :disabled="group.allAccepted || caseAccepting[group._caseDocId] || !isLabTechnologist"
                  @click="acceptCase(group._caseDocId)"
                >
                  <span v-if="caseAccepting[group._caseDocId]" class="btn-spinner"></span>
                  {{ group.allAccepted ? '✓ Accepted' : caseAccepting[group._caseDocId] ? 'Accepting...' : 'Accept Case' }}
                </button>
              </div>
              <div class="report-case__rule"></div>
              <div class="rpt-col-header">
                <div class="col col-name">Test Name</div>
                <div v-if="group.hasPrevResult" class="col col-prev">Last Run</div>
                <div
                  v-for="col in group.prevColumns"
                  :key="col.key"
                  class="col col-prevdate"
                >
                  <span class="prevdate-date">{{ formatShortDate(col.date) }}</span>
                  <span v-if="col.accession" class="prevdate-acc">{{ col.accession }}</span>
                </div>
                <div v-if="group.hasTrend" class="col col-trend">Trend</div>
                <div class="col col-result">Result</div>
                <div class="col col-range">Ref. Range</div>
                <div class="col col-flag">Flag</div>
                <div class="col col-action">Action</div>
              </div>
            </div>

            <!-- Panels + rows -->
            <div v-for="panel in group.panels" :key="panel.panel_name" class="report-panel">
              <div class="report-panel__name">{{ panel.panel_name }}</div>
              <div
                v-for="row in panel.rows"
                :key="row._id"
                class="result-item"
              >
              <div class="result-row" :class="flagRowClass(row.flag)">
                <div class="col col-name" :title="row.test_name">{{ row.test_name }}</div>
                <div v-if="group.hasPrevResult" class="col col-prev cell-num">{{ row.prev_result ?? '—' }}</div>
                <div
                  v-for="col in group.prevColumns"
                  :key="col.key"
                  class="col col-prevdate cell-num"
                >{{ prevCellValue(group, row, col.key) }}</div>
                <div v-if="group.hasTrend" class="col col-trend">
                  <button
                    v-if="group.trends[row._id]"
                    type="button"
                    class="trend-spark"
                    :class="{ open: expandedTrends[row._id] }"
                    :title="`${expandedTrends[row._id] ? 'Hide' : 'Show'} ${row.test_name} trend`"
                    @click="toggleTrend(row._id)"
                  >
                    <svg viewBox="0 0 64 22" width="64" height="22" aria-hidden="true">
                      <path class="trend-spark__line" :d="group.trends[row._id].spark.path" />
                      <circle
                        class="trend-spark__now"
                        :class="trendToneClass(row.flag)"
                        :cx="group.trends[row._id].spark.lastX"
                        :cy="group.trends[row._id].spark.lastY"
                        r="2.6"
                      />
                    </svg>
                    <span class="trend-spark__dir" :class="'is-' + group.trends[row._id].direction">
                      {{ group.trends[row._id].direction === 'up' ? '▲' : group.trends[row._id].direction === 'down' ? '▼' : '–' }}
                    </span>
                  </button>
                  <span v-else class="cell-muted">—</span>
                </div>
                <div class="col col-result cell-num">{{ row.result ?? '—' }}</div>
                <div class="col col-range cell-num">{{ row.reference_range || '—' }}</div>
                <div class="col col-flag">
                  <span v-if="row.flag && flagShortLabel(row.flag)" class="flag-pill" :class="flagPillClass(row.flag)" :title="row.flag">
                    {{ flagShortLabel(row.flag) }}
                  </span>
                  <span v-else class="cell-muted">—</span>
                </div>
                <div class="col col-action">
                  <span v-if="row._applying" class="row-applying">
                    <span class="btn-spinner"></span>
                    {{ row._pendingAction === 'HOLD' ? 'Holding...' : 'Accepting...' }}
                  </span>
                  <!-- Native select: one per row, so it has to stay light for large instruments -->
                  <select
                    v-else-if="row.review_status !== 'ACCEPTED'"
                    v-model="row._pendingAction"
                    class="action-select-native"
                    :class="{ 'is-held': row.review_status === 'HELD' }"
                    :disabled="!isLabTechnologist || row._applying"
                    @change="onActionChange(row._pendingAction, row)"
                  >
                    <option value="ACCEPT">Accept</option>
                    <option value="HOLD">Hold</option>
                  </select>
                  <span v-else class="done-label">✓</span>
                </div>
              </div>

              <!-- Expanded trend chart for this test -->
              <div v-if="expandedTrends[row._id] && group.trends[row._id]" class="result-trend">
                <result-trend-chart
                  :test-name="row.test_name"
                  :points="group.trends[row._id].points"
                  :reference-range="row.reference_range"
                  :tone="trendToneClass(row.flag)"
                  @close="toggleTrend(row._id)"
                />
              </div>

              <!-- Per-test comment (only for critical / abnormal results) -->
              <div v-if="isCommentPresetFlag(row.flag)" class="result-comment">
                <el-autocomplete
                  v-model="row.comment"
                  :fetch-suggestions="(q, cb) => queryCommentSuggestions(q, cb, row)"
                  :trigger-on-focus="isCommentPresetFlag(row.flag)"
                  :fit-input-width="true"
                  value-key="value"
                  clearable
                  popper-class="result-comment-suggestions"
                  class="result-comment-input"
                  placeholder="Add comment for this test..."
                  :disabled="!isLabTechnologist"
                  @select="saveResultComment(group, row)"
                  @keyup.enter="saveResultComment(group, row)"
                  @blur="saveResultComment(group, row)"
                >
                  <template #default="{ item }">
                    <div class="preset-item">
                      <svg
                        class="preset-item__icon"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        aria-hidden="true"
                      >
                        <path
                          fill="currentColor"
                          d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1m-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1"
                        />
                      </svg>
                      <span class="preset-item__text">{{ item.value }}</span>
                    </div>
                  </template>
                </el-autocomplete>
                <span v-if="row._commentSaving" class="result-comment-status"><span class="btn-spinner"></span> Saving…</span>
                <span v-else-if="row._commentSaved" class="result-comment-status saved">Saved ✓</span>
                <span v-else-if="rowNeedsComment(row)" class="result-comment-status required">Comment required *</span>
              </div>
              </div>
            </div>

            <!-- Case-level report comment -->
            <div class="case-report-comment">
              <span class="report-comment-label">Report Comments</span>
              <input
                v-model="caseComments[group._caseDocId]"
                type="text"
                placeholder="Add report comment..."
                class="report-comment-input"
                @keyup.enter="saveComment(group._caseDocId)"
              />
              <button
                class="report-comment-save"
                :disabled="caseSaving[group._caseDocId] || !isLabTechnologist"
                @click="saveComment(group._caseDocId)"
              >
                <span v-if="caseSaving[group._caseDocId]" class="btn-spinner"></span>
                {{ caseSaving[group._caseDocId] ? 'Saving...' : 'Save' }}
              </button>
            </div>

            <!-- Gap between cases -->
            <div v-if="idx < visibleResultGroups.length - 1" class="report-case__gap"></div>
          </div>
        </div>

        <div v-if="renderedCaseCount < groupedResults.length" class="iq-rendering-more">
          <span class="btn-spinner"></span>
          Loading {{ groupedResults.length - renderedCaseCount }} more case(s)…
        </div>
      </template>
    </div>
    </div>

    <!-- ══════════ PENDING TESTS TAB ══════════ -->
    <div v-show="activeTab === 'PENDING'" class="iq-main">
      <div class="iq-page-header">
        <h2>Pending Tests</h2>
        <div class="iq-page-header__actions">
          <el-button
            size="small"
            type="success"
            :disabled="!filteredPending.length"
            @click="openPendingReport"
          >
            Export / Print
          </el-button>
          <el-button size="small" :loading="pendingLoading" @click="loadPendingTests">
            Refresh
          </el-button>
        </div>
      </div>

      <div class="pt-filters">
        <el-autocomplete
          v-model="pendingFilters.test"
          class="pt-filter"
          size="small"
          placeholder="Search test / panel..."
          clearable
          :fetch-suggestions="queryTestSuggestions"
          :trigger-on-focus="true"
          value-key="value"
        />

        <el-select
          v-model="pendingFilters.hospital"
          class="pt-filter"
          size="small"
          placeholder="All Ordering Facilities"
          clearable
          filterable
        >
          <el-option
            v-for="f in filterOptions.facilities"
            :key="f.id"
            :label="f.name"
            :value="f.id"
          />
        </el-select>

        <el-date-picker
          v-model="pendingFilters.dateRange"
          class="pt-filter pt-filter--date"
          size="small"
          type="daterange"
          unlink-panels
          range-separator="to"
          start-placeholder="From"
          end-placeholder="To"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
        />

        <el-button
          v-if="pendingFilters.test || pendingFilters.hospital || pendingFilters.dateRange"
          size="small"
          text
          @click="clearPendingFilters"
        >
          Clear
        </el-button>
      </div>

      <div
        class="listings listings__table"
        v-loading="pendingLoading"
      >
        <el-table
          :data="pagedPending"
          height="calc(100vh - 360px)"
          empty-text="No pending tests found."
        >
          <el-table-column
            prop="accession_id"
            label="Accession ID"
            min-width="130"
            fixed="left"
          >
            <template #default="scope">{{ scope.row.accession_id || "—" }}</template>
          </el-table-column>
          <el-table-column label="Patient" min-width="150">
            <template #default="scope">{{ scope.row.patient_name || "—" }}</template>
          </el-table-column>
          <el-table-column label="DOB" min-width="110">
            <template #default="scope">{{ scope.row.patient_dob ? formatDob(scope.row.patient_dob) : "—" }}</template>
          </el-table-column>
          <el-table-column label="Ordering Physician" min-width="150">
            <template #default="scope">{{ scope.row.ordering_physician || "—" }}</template>
          </el-table-column>
          <el-table-column label="Ordering Facility" min-width="170">
            <template #default="scope">{{ scope.row.hospital_name || "—" }}</template>
          </el-table-column>
          <el-table-column label="Pending Tests" min-width="280">
            <template #default="scope">{{ (scope.row.pending_tests || []).join(", ") || "—" }}</template>
          </el-table-column>
        </el-table>
      </div>

      <div class="listings listings__pagination">
        <el-pagination
          v-model:currentPage="pendingPage"
          v-model:page-size="pendingPageSize"
          :page-sizes="[25, 50, 75, 100]"
          layout=" prev, pager, next, jumper, sizes ,total"
          :total="filteredPending.length"
          @size-change="handlePendingSizeChange"
        />
      </div>
    </div>

    <!-- ══════════ CRITICAL TESTS TAB ══════════ -->
    <div v-show="activeTab === 'CRITICAL'" class="iq-main">
      <div class="iq-page-header">
        <h2>Critical Tests</h2>
        <div class="iq-page-header__actions">
          <el-button
            size="small"
            type="success"
            :loading="criticalExporting"
            :disabled="!criticalTotal"
            @click="openCriticalReport"
          >
            Export / Print
          </el-button>
          <el-button size="small" :loading="criticalLoading" @click="loadCriticalTests(false)">
            Refresh
          </el-button>
        </div>
      </div>

      <div class="pt-filters">
        <el-input
          v-model="criticalFilters.search"
          class="pt-filter"
          size="small"
          placeholder="Search accession / patient..."
          clearable
          @input="onCriticalSearchInput"
        />

        <el-select
          v-model="criticalFilters.hospital"
          class="pt-filter"
          size="small"
          placeholder="All Ordering Facilities"
          clearable
          filterable
          remote
          reserve-keyword
          :remote-method="remoteFacility"
          :loading="hospitalLoading"
          @change="loadCriticalTests()"
          @clear="loadCriticalTests()"
        >
          <el-option
            v-for="h in hospitalsData"
            :key="h._id"
            :label="h.name"
            :value="h._id"
          />
        </el-select>

        <el-date-picker
          v-model="criticalFilters.dateRange"
          class="pt-filter pt-filter--date"
          size="small"
          type="daterange"
          unlink-panels
          range-separator="to"
          start-placeholder="From"
          end-placeholder="To"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
          @change="loadCriticalTests()"
        />

        <el-button
          v-if="criticalFilters.search || criticalFilters.hospital || criticalFilters.dateRange"
          size="small"
          text
          @click="clearCriticalFilters"
        >
          Clear
        </el-button>
      </div>

      <div
        class="listings listings__table"
        v-loading="criticalLoading"
      >
        <el-table
          :data="pagedCritical"
          height="calc(100vh - 360px)"
          empty-text="No critical tests found."
          @sort-change="onCriticalSortChange"
        >
          <el-table-column
            prop="accession_id"
            label="Accession ID"
            min-width="130"
            fixed="left"
            sortable="custom"
          >
            <template #default="scope">{{ scope.row.accession_id || "—" }}</template>
          </el-table-column>
          <el-table-column prop="name" label="Patient" min-width="150" sortable="custom">
            <template #default="scope">{{ scope.row.name || "—" }}</template>
          </el-table-column>
          <el-table-column prop="date" label="Critical Date" min-width="150" sortable="custom">
            <template #default="scope">{{ formatCriticalDate(scope.row.date) }}</template>
          </el-table-column>
          <el-table-column label="Ordering Facility" min-width="170">
            <template #default="scope">{{ scope.row.facility_name || "—" }}</template>
          </el-table-column>
          <el-table-column label="Critical Values" min-width="260">
            <template #default="scope">{{ criticalValuesText(scope.row) || "—" }}</template>
          </el-table-column>
          <el-table-column label="Report Comments" min-width="180">
            <template #default="scope">{{ scope.row.comments || "—" }}</template>
          </el-table-column>
          <el-table-column label="Test Comments" min-width="220">
            <template #default="scope">
              <template v-if="(scope.row.test_comments || []).length">
                <div v-for="(tc, i) in scope.row.test_comments" :key="i" class="test-comment-line">
                  <strong>{{ tc.test_name }}:</strong> {{ tc.comment }}
                </div>
              </template>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="Case Status" min-width="130">
            <template #default="scope">{{ scope.row.case_status ? formatCaseStatus(scope.row.case_status) : "—" }}</template>
          </el-table-column>
        </el-table>
      </div>

      <div class="listings listings__pagination">
        <el-pagination
          v-model:currentPage="criticalPage"
          v-model:page-size="criticalPageSize"
          :page-sizes="[25, 50, 75, 100]"
          layout=" prev, pager, next, jumper, sizes ,total"
          :total="criticalTotal"
          @current-change="loadCriticalTests(false)"
          @size-change="handleCriticalSizeChange"
        />
      </div>
    </div>

    <!-- ══════════ SEARCH BY TEST TAB ══════════ -->
    <div v-show="activeTab === 'SEARCH'" class="iq-main">
      <div class="iq-page-header">
        <h2>Search by Test</h2>
        <div class="iq-page-header__actions">
          <el-button
            size="small"
            type="success"
            :loading="searchExporting"
            :disabled="!searchTotal"
            @click="openSearchReport"
          >
            Export / Print
          </el-button>
          <el-button size="small" :loading="searchLoading" @click="loadSearchResults">
            Refresh
          </el-button>
        </div>
      </div>

      <div class="pt-filters">
        <el-autocomplete
          v-model="searchFilters.test"
          class="pt-filter"
          size="small"
          placeholder="Search test / panel..."
          clearable
          :fetch-suggestions="queryTestSuggestions"
          :trigger-on-focus="true"
          value-key="value"
          @select="onSearchFilterChange"
          @input="onSearchTestInput"
          @clear="onSearchFilterChange"
        />

        <el-select
          v-model="searchFilters.hospital"
          class="pt-filter"
          size="small"
          placeholder="All Ordering Facilities"
          clearable
          filterable
          remote
          reserve-keyword
          :remote-method="remoteFacility"
          :loading="hospitalLoading"
          @change="onSearchFilterChange"
          @clear="onSearchFilterChange"
        >
          <el-option
            v-for="h in hospitalsData"
            :key="h._id"
            :label="h.name"
            :value="h._id"
          />
        </el-select>

        <el-date-picker
          v-model="searchFilters.dateRange"
          class="pt-filter pt-filter--date"
          size="small"
          type="daterange"
          unlink-panels
          range-separator="to"
          start-placeholder="From"
          end-placeholder="To"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
          @change="onSearchFilterChange"
        />

        <el-button
          v-if="searchFilters.test || searchFilters.hospital || searchFilters.dateRange"
          size="small"
          text
          @click="clearSearchFilters"
        >
          Clear
        </el-button>
      </div>

      <div class="search-results" v-loading="searchLoading">
        <div v-if="!groupedSearch.length && !searchLoading" class="iq-empty">
          No results found.
        </div>

        <div v-else class="report-list">
          <div
            v-for="group in groupedSearch"
            :key="group._caseDocId"
            class="report-case"
          >
            <div class="report-case__header">
              <div class="report-case__patient">
                <span class="rpt-label">ACC</span>
                <span class="rpt-dot">·</span>
                <span class="rpt-accession">{{ group.accession_id || "—" }}</span>
                <span class="rpt-sep"></span>
                <span class="rpt-name">{{ group.patient_name || "—" }}</span>
                <span class="rpt-dot">·</span>
                <span class="rpt-meta">{{ group.patient_gender === 'MALE' ? 'M' : group.patient_gender === 'FEMALE' ? 'F' : (group.patient_gender || '—') }}</span>
                <template v-if="group.patient_dob">
                  <span class="rpt-dot">·</span>
                  <span class="rpt-meta">{{ formatDob(group.patient_dob) }}</span>
                </template>
                <template v-if="group.facility_name">
                  <span class="rpt-dot">·</span>
                  <span class="rpt-meta">{{ group.facility_name }}</span>
                </template>
                <template v-if="group.collection_date">
                  <span class="rpt-dot">·</span>
                  <span class="rpt-meta">{{ formatDate(group.collection_date) }}</span>
                </template>
              </div>
            </div>
            <div class="report-case__rule"></div>

            <div v-for="panel in group.panels" :key="panel.panel_name" class="report-panel">
              <div class="report-panel__name">{{ panel.panel_name }}</div>
              <table class="search-test-table">
                <thead>
                  <tr>
                    <th class="stt-name">Test Name</th>
                    <th class="stt-result">Result</th>
                    <th class="stt-units">Units</th>
                    <th class="stt-range">Reference Range</th>
                    <th class="stt-flag">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in panel.rows"
                    :key="row._id || row.result_name"
                    :class="flagRowClass(row.flag)"
                  >
                    <td class="stt-name">{{ row.result_name || row.test_name_code || "—" }}</td>
                    <td class="stt-result">{{ row.result ?? "—" }}</td>
                    <td class="stt-units">{{ row.units || "—" }}</td>
                    <td class="stt-range">{{ row.reference_range || "—" }}</td>
                    <td class="stt-flag">
                      <span v-if="row.flag && flagShortLabel(row.flag)" class="flag-pill" :class="flagPillClass(row.flag)" :title="row.flag">
                        {{ flagShortLabel(row.flag) }}
                      </span>
                      <span v-else class="cell-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="listings listings__pagination">
        <el-pagination
          v-model:currentPage="searchPage"
          v-model:page-size="searchPageSize"
          :page-sizes="[25, 50, 75, 100]"
          layout=" prev, pager, next, jumper, sizes ,total"
          :total="searchTotal"
          @size-change="handleSearchSizeChange"
          @current-change="handleSearchCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment-timezone";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";
import { ElMessage } from "element-plus";
import ResultTrendChart from "./ResultTrendChart.vue";

// Numeric value of a result for trending ("12.4", "1,250", "<0.10" -> 0.1);
// qualitative results ("Negative", "Reactive") return null
function parseNumeric(val) {
  if (val === null || val === undefined) return null;
  const m = String(val).trim().match(/^[<>≤≥]?=?\s*(-?\d[\d,]*\.?\d*|-?\.\d+)$/);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

export default {
  name: "InstrumentQueueDashboard",
  components: { ResultTrendChart },
  mixins: [UserPermissionsHelper],
  data() {
    return {
      activeTab: "RELEASE",
      dashboardLoading: false,
      // Pending Tests tab
      pendingLoading: false,
      pendingLoaded: false,
      pendingTests: [],
      pendingFilters: { test: "", hospital: "", dateRange: null },
      pendingPage: 1,
      pendingPageSize: 25,
      filterOptions: { tests: [], facilities: [] },
      // Critical Tests tab (data comes from the blood interface service)
      criticalLoading: false,
      criticalLoaded: false,
      criticalTests: [],
      criticalFilters: { search: "", hospital: "", dateRange: null },
      criticalPage: 1,
      criticalPageSize: 25,
      criticalTotal: 0,
      criticalSort: { sort_by: "", sort_order: "" },
      criticalExporting: false,
      // Search by Test tab (server-side paginated blood-results search)
      searchLoading: false,
      searchLoaded: false,
      searchExporting: false,
      searchResults: [],
      searchTotal: 0,
      searchFilters: { test: "", hospital: "", dateRange: null },
      searchPage: 1,
      searchPageSize: 25,
      searchSearchTimer: null,
      hospitalsData: [],
      hospitalLoading: false,
      criticalSearchTimer: null,
      resultsLoading: false,
      // Instrument shown in the active workspace tab (null = overview tiles)
      resultsTab: { instrumentId: null, name: "" },
      // Instruments opened as workspace tabs; each remembers its own filters
      openTabs: [],
      // row._id -> true while that test's trend chart is expanded
      expandedTrends: {},
      resultsFilter: "",
      // Cases are added to the page in batches (all end up on one page) so large
      // instruments don't freeze the browser rendering hundreds of cases at once
      renderedCaseCount: 0,
      renderTimer: null,
      reviewFilter: "PENDING",
      results: [],
      acceptAllLoading: false,
      caseComments: {},
      caseSaving: {},
      caseAccepting: {},
      casePreviousResults: {},
      categoryOptions: [
        { label: "All", value: "" },
        { label: "New", value: "NEW" },
        { label: "Old", value: "OLD" },
      ],
      statusOptions: [
        { label: "All Statuses", value: "" },
        { label: "Pending", value: "PENDING" },
        { label: "Accepted", value: "ACCEPTED" },
      ],
      // Prefilled comment options for lab technicians (they can still type their own)
      commentPresets: [
        "Critical value reported to at on. Results repeated and verified.",
        "Results repeated and verified.",
        "Results reported to at on. Results repeated and verified.",
        "Results reported to at on. Sample being sent to Labcorp for RPR confirmation.",
      ],
    };
  },
  computed: {
    ...mapGetters("instrumentQueue", ["getDashboard", "getBatchResults"]),
    isLabTechnologist() {
      return this.currentUserType === "LAB_TECHNOLOGIST";
    },
    dashboard() {
      return this.getDashboard;
    },
    // Client-side filtering of the pending tests list (test / facility / date range)
    filteredPending() {
      const { test, hospital, dateRange } = this.pendingFilters;
      const testQ = (test || "").toLowerCase();
      const [from, to] = dateRange || [];
      const fromTs = from ? new Date(from + "T00:00:00").getTime() : null;
      const toTs = to ? new Date(to + "T23:59:59.999").getTime() : null;

      return this.pendingTests.filter((row) => {
        if (hospital && String(row.hospital_id) !== String(hospital)) return false;
        if (testQ && !(row.pending_tests || []).some((t) => (t || "").toLowerCase().includes(testQ))) {
          return false;
        }
        if (fromTs || toTs) {
          if (!row.collection_date) return false;
          const ts = new Date(row.collection_date).getTime();
          if (fromTs && ts < fromTs) return false;
          if (toTs && ts > toTs) return false;
        }
        return true;
      });
    },
    // Current page slice shown in the pending-tests table (export still uses the
    // full filteredPending list, so it always covers every matching row).
    pagedPending() {
      const start = (this.pendingPage - 1) * this.pendingPageSize;
      return this.filteredPending.slice(start, start + this.pendingPageSize);
    },
    // Current page slice shown in the critical-tests table (the server already
    // applies the filters, so criticalTests is the full matching set to export).
    // The API pages the list, so criticalTests already holds just the current page
    pagedCritical() {
      return this.criticalTests;
    },
    // Group each matching case's blood results by panel (test_name_code) for the
    // read-only "each case display" layout in the Search by Test tab.
    groupedSearch() {
      return this.searchResults.map((caseDoc) => {
        const panelMap = {};
        const order = [];
        (caseDoc.results || []).forEach((r) => {
          const key = r.test_name_code || r.result_name || "General";
          if (!panelMap[key]) {
            panelMap[key] = { panel_name: key, rows: [] };
            order.push(key);
          }
          panelMap[key].rows.push(r);
        });
        return {
          _caseDocId: String(caseDoc._id || caseDoc.case_id),
          case_id: caseDoc.case_id,
          accession_id: caseDoc.accession_id,
          patient_name: [
            caseDoc.patient_first_name,
            caseDoc.patient_middle_name,
            caseDoc.patient_last_name,
          ]
            .filter(Boolean)
            .join(" "),
          patient_gender: caseDoc.patient_gender,
          patient_dob: caseDoc.patient_dob,
          facility_name: caseDoc.facility_name,
          collection_date: caseDoc.collection_date,
          panels: order.map((k) => panelMap[k]),
        };
      });
    },
    visibleResultGroups() {
      return this.groupedResults.slice(0, this.renderedCaseCount);
    },
    groupedResults() {
      const caseMap = {};
      const caseOrder = [];

      this.results.forEach((r) => {
        const key = r._caseDocId;
        if (!caseMap[key]) {
          caseMap[key] = {
            _caseDocId: r._caseDocId,
            accession_id: r.accession_id,
            patient_name: this.patientName(r),
            patient_gender: r.patient_gender || "",
            patient_dob: r.patient_dob || "",
            panelMap: {},
            panelOrder: [],
          };
          caseOrder.push(key);
        }

        const panelKey = r.test_name_code || r.panel_code || "General";
        const entry = caseMap[key];
        if (!entry.panelMap[panelKey]) {
          entry.panelMap[panelKey] = {
            panel_name: r.test_name_code || r.panel_name || "General",
            rows: [],
          };
          entry.panelOrder.push(panelKey);
        }
        entry.panelMap[panelKey].rows.push(r);
      });

      const groups = caseOrder.map((key) => {
        const entry = caseMap[key];
        const panels = entry.panelOrder.map((k) => entry.panelMap[k]);
        panels.sort((a, b) => (a.panel_name || "").localeCompare(b.panel_name || ""));
        const allRows = panels.flatMap((p) => p.rows);

        // Build previous history: one column per prior case (keyed by
        // case/accession, since multiple prior cases can share a collection
        // date) and a per-test lookup of result_code -> columnKey -> result.
        const prevList = this.casePreviousResults[entry._caseDocId] || [];
        const prevLookup = {};
        const colMap = new Map();
        prevList.forEach((p) => {
          const colKey =
            (p.case_id ? String(p.case_id) : "") + "|" + (p.accession_id || "");
          if (!colMap.has(colKey)) {
            colMap.set(colKey, {
              key: colKey,
              date: p.collection_date || null,
              accession: p.accession_id || "",
            });
          }
          const code = p.result_code || p.test_name;
          if (!prevLookup[code]) prevLookup[code] = {};
          prevLookup[code][colKey] = p.result;
        });
        const prevColumns = [...colMap.values()].sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          if (db !== da) return db - da;
          return (a.accession || "").localeCompare(b.accession || "");
        });

        // Per-test trend: prior runs oldest -> newest, then today's result.
        // Only numeric values plot; a test needs two of them to show a trend.
        const chronological = [...prevColumns].reverse();
        const trends = {};
        allRows.forEach((r) => {
          const byCol = prevLookup[r.result_code || r.test_name] || {};
          const points = chronological
            .map((c) => ({ date: c.date, accession: c.accession, raw: byCol[c.key], current: false }))
            .concat({ date: r.collection_date, accession: entry.accession_id, raw: r.result, current: true })
            .map((p) => ({ ...p, value: parseNumeric(p.raw) }))
            .filter((p) => p.value !== null);
          if (points.length < 2) return;
          trends[r._id] = {
            points,
            spark: this.sparkGeometry(points),
            direction: this.trendDirection(points),
          };
        });

        return {
          _caseDocId: entry._caseDocId,
          accession_id: entry.accession_id,
          patient_name: entry.patient_name,
          patient_gender: entry.patient_gender,
          patient_dob: entry.patient_dob,
          panels,
          trends,
          hasTrend: Object.keys(trends).length > 0,
          totalTests: allRows.length,
          allAccepted: allRows.every((r) => r.review_status === "ACCEPTED"),
          hasPrevResult: allRows.some(
            (r) =>
              r.prev_result !== null &&
              r.prev_result !== undefined &&
              r.prev_result !== ""
          ),
          prevColumns,
          prevLookup,
          criticalCount: allRows.filter((r) => this.isCriticalFlag(r.flag)).length,
        };
      });

      // Cases with critical results always come first (most criticals on top); ties keep their order
      return groups
        .map((g, i) => ({ g, i }))
        .sort((a, b) => b.g.criticalCount - a.g.criticalCount || a.i - b.i)
        .map(({ g }) => g);
    },
  },
  watch: {
    // Pending tests filter client-side, so reset to page 1 when the filters
    // change to avoid landing on an out-of-range page.
    pendingFilters: {
      deep: true,
      handler() {
        this.pendingPage = 1;
      },
    },
  },
  beforeUnmount() {
    this.stopProgressiveRender();
  },
  async mounted() {
    await this.loadDashboard();
  },
  methods: {
    ...mapActions("instrumentQueue", [
      "fetchDashboard",
      "fetchInstrumentResults",
      "bulkReviewInstrument",
      "reviewCase",
      "reviewSingleResult",
      "updateCaseComment",
      "updateResultComment",
      "fetchPendingTests",
      "fetchPendingTestsFilters",
      "fetchCriticalTests",
      "fetchBloodResultsSearch",
    ]),
    ...mapActions("hospitals", ["fetchAllHospitals"]),

    async loadDashboard() {
      this.dashboardLoading = true;
      await this.fetchDashboard();
      this.dashboardLoading = false;
    },

    async switchToPending() {
      this.activeTab = "PENDING";
      if (!this.pendingLoaded) {
        await Promise.all([this.loadPendingTests(), this.loadPendingFilters()]);
        this.pendingLoaded = true;
      }
    },

    async loadPendingTests() {
      this.pendingLoading = true;
      try {
        this.pendingTests = await this.fetchPendingTests();
      } catch {
        this.pendingTests = [];
        ElMessage({ type: "error", message: "Failed to load pending tests.", duration: 2000 });
      } finally {
        this.pendingLoading = false;
      }
    },

    async loadPendingFilters() {
      try {
        this.filterOptions = await this.fetchPendingTestsFilters();
      } catch {
        this.filterOptions = { tests: [], facilities: [] };
      }
    },

    clearPendingFilters() {
      this.pendingFilters = { test: "", hospital: "", dateRange: null };
    },

    handlePendingSizeChange(size) {
      this.pendingPageSize = size;
      this.pendingPage = 1;
    },

    handleCriticalSizeChange(size) {
      this.criticalPageSize = size;
      this.criticalPage = 1;
      this.loadCriticalTests(false);
    },

    // Suggest matching panel / test names as the user types (filters the table live)
    queryTestSuggestions(queryString, cb) {
      const q = (queryString || "").toLowerCase();
      const matches = (this.filterOptions.tests || [])
        .filter((t) => !q || (t || "").toLowerCase().includes(q))
        .map((t) => ({ value: t }));
      cb(matches);
    },

    // ── Critical Tests tab ──
    async switchToCritical() {
      this.activeTab = "CRITICAL";
      if (!this.criticalLoaded) {
        await Promise.all([this.loadCriticalTests(), this.loadFacilities()]);
        this.criticalLoaded = true;
      }
    },

    // Current filters as API params (shared by the paged list and the export)
    criticalFilterParams() {
      const params = { is_critical: true };
      const { search, hospital, dateRange } = this.criticalFilters;
      if (search) params.search_string = search;
      if (hospital) params.hospital = hospital;
      if (dateRange && dateRange[0]) params.fromDate = dateRange[0];
      if (dateRange && dateRange[1]) params.toDate = dateRange[1];
      if (this.criticalSort.sort_by) {
        params.sort_by = this.criticalSort.sort_by;
        params.sort_order = this.criticalSort.sort_order;
      }
      return params;
    },

    // Column header sort → API sort (whole list, not just the page); export follows it too
    onCriticalSortChange({ prop, order }) {
      this.criticalSort = order
        ? { sort_by: prop, sort_order: order === "ascending" ? "asc" : "desc" }
        : { sort_by: "", sort_order: "" };
      this.loadCriticalTests();
    },

    formatCriticalDate(value) {
      return value && moment(value).isValid() ? moment(value).format("MM/DD/YYYY h:mm A") : "—";
    },

    // Loads one page from the API. Filter changes go back to page 1; paging/refresh keep it.
    async loadCriticalTests(resetPage = true) {
      if (resetPage) this.criticalPage = 1;
      this.criticalLoading = true;
      try {
        const res = await this.fetchCriticalTests({
          ...this.criticalFilterParams(),
          page: this.criticalPage,
          limit: this.criticalPageSize,
        });
        this.criticalTests = res.data || [];
        this.criticalTotal = res.total || 0;
      } catch {
        this.criticalTests = [];
        this.criticalTotal = 0;
        ElMessage({ type: "error", message: "Failed to load critical tests.", duration: 2000 });
      } finally {
        this.criticalLoading = false;
      }
    },

    onCriticalSearchInput() {
      clearTimeout(this.criticalSearchTimer);
      this.criticalSearchTimer = setTimeout(() => this.loadCriticalTests(), 500);
    },

    async loadFacilities(query) {
      this.hospitalLoading = true;
      try {
        const params = { page: 1, limit: 20 };
        if (query && typeof query === "string") params.search_string = query;
        await this.fetchAllHospitals(params);
        const store = this.$store.getters["hospitals/getAllHospitals"];
        this.hospitalsData = (store && store.data) || [];
      } catch {
        this.hospitalsData = [];
      } finally {
        this.hospitalLoading = false;
      }
    },

    remoteFacility(query) {
      this.loadFacilities(query);
    },

    clearCriticalFilters() {
      this.criticalFilters = { search: "", hospital: "", dateRange: null };
      this.loadCriticalTests();
    },

    // ── Search by Test tab ──
    async switchToSearch() {
      this.activeTab = "SEARCH";
      if (!this.searchLoaded) {
        // loadPendingFilters populates filterOptions.tests, which the test-search
        // autocomplete (queryTestSuggestions) uses for its dropdown.
        await Promise.all([
          this.loadSearchResults(),
          this.loadFacilities(),
          this.loadPendingFilters(),
        ]);
        this.searchLoaded = true;
      }
    },

    // Build the request params from the current filters (shared by list + export)
    buildSearchParams(overrides = {}) {
      const params = { page: this.searchPage, limit: this.searchPageSize };
      const { test, hospital, dateRange } = this.searchFilters;
      if (test && test.trim()) params.test = test.trim();
      if (hospital) params.hospital = hospital;
      if (dateRange && dateRange[0]) params.from_date = dateRange[0];
      if (dateRange && dateRange[1]) params.to_date = dateRange[1];
      return { ...params, ...overrides };
    },

    async loadSearchResults() {
      this.searchLoading = true;
      try {
        const res = await this.fetchBloodResultsSearch(this.buildSearchParams());
        this.searchResults = res.data || [];
        this.searchTotal = res.total || 0;
      } catch {
        this.searchResults = [];
        this.searchTotal = 0;
        ElMessage({ type: "error", message: "Failed to load results.", duration: 2000 });
      } finally {
        this.searchLoading = false;
      }
    },

    onSearchTestInput() {
      clearTimeout(this.searchSearchTimer);
      this.searchSearchTimer = setTimeout(() => {
        this.searchPage = 1;
        this.loadSearchResults();
      }, 500);
    },

    onSearchFilterChange() {
      this.searchPage = 1;
      this.loadSearchResults();
    },

    clearSearchFilters() {
      this.searchFilters = { test: "", hospital: "", dateRange: null };
      this.searchPage = 1;
      this.loadSearchResults();
    },

    handleSearchSizeChange(size) {
      this.searchPageSize = size;
      this.searchPage = 1;
      this.loadSearchResults();
    },

    handleSearchCurrentChange(page) {
      this.searchPage = page;
      this.loadSearchResults();
    },

    // Open a printable report of every matching case (all results / filtered)
    async openSearchReport() {
      this.searchExporting = true;
      try {
        // Pull the full matching set (ignore pagination) for the printout
        const res = await this.fetchBloodResultsSearch(
          this.buildSearchParams({ page: 1, limit: 100000 })
        );
        const cases = res.data || [];
        if (!cases.length) {
          ElMessage({ type: "warning", message: "No results to export.", duration: 2000 });
          return;
        }
        this.printSearchReport(cases);
      } catch {
        ElMessage({ type: "error", message: "Failed to export results.", duration: 2000 });
      } finally {
        this.searchExporting = false;
      }
    },

    printSearchReport(cases) {
      const esc = (v) =>
        String(v ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      let body = "";
      cases.forEach((c) => {
        const name = [c.patient_first_name, c.patient_middle_name, c.patient_last_name]
          .filter(Boolean)
          .join(" ");
        const meta = [
          c.facility_name,
          c.patient_dob ? this.formatDob(c.patient_dob) : "",
          c.collection_date ? this.formatDate(c.collection_date) : "",
        ]
          .filter(Boolean)
          .join(" · ");

        // Group this case's rows by panel (test_name_code)
        const panelMap = {};
        const order = [];
        (c.results || []).forEach((r) => {
          const key = r.test_name_code || r.result_name || "General";
          if (!panelMap[key]) {
            panelMap[key] = [];
            order.push(key);
          }
          panelMap[key].push(r);
        });

        body += `<div class="case-head">${esc(c.accession_id || "—")} &middot; ${esc(name || "—")}${meta ? " &middot; " + esc(meta) : ""}</div>`;
        order.forEach((panel) => {
          body += `<div class="panel-name">${esc(panel)}</div>`;
          body += `<table class="rpt"><thead><tr>
            <th class="c-name">Test Name</th>
            <th class="c-res">Result</th>
            <th class="c-units">Units</th>
            <th class="c-range">Reference Range</th>
            <th class="c-flag">Flag</th>
          </tr></thead><tbody>`;
          panelMap[panel].forEach((r) => {
            body += `<tr>
              <td class="c-name">${esc(r.result_name || r.test_name_code || "")}</td>
              <td class="c-res">${esc(r.result ?? "")}</td>
              <td class="c-units">${esc(r.units || "")}</td>
              <td class="c-range">${esc(r.reference_range || "")}</td>
              <td class="c-flag">${esc(r.flag || "")}</td>
            </tr>`;
          });
          body += `</tbody></table>`;
        });
      });

      const printedOn = moment().format("MM/DD/YYYY HH:mm");
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Test Results Report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 24px; font-size: 12px; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .sub { color: #555; font-size: 11px; margin-bottom: 16px; }
    .case-head { font-weight: 700; font-size: 12px; background: #f0f0f0; padding: 5px 6px; border-top: 1px solid #999; margin-top: 12px; }
    .panel-name { font-weight: 700; font-size: 12px; padding: 6px 6px 2px; }
    table.rpt { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    table.rpt th { text-align: left; font-size: 10px; text-transform: uppercase; color: #333; border-bottom: 1px solid #333; padding: 2px 6px; }
    table.rpt td { padding: 3px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
    .c-name { width: 28%; }
    .c-res { width: 16%; }
    .c-units { width: 14%; }
    .c-range { width: 30%; }
    .c-flag { width: 12%; }
    .footer { margin-top: 20px; font-size: 10px; color: #666; border-top: 1px solid #ccc; padding-top: 6px; }
    @media print { body { margin: 12px; } .no-print { display: none; } }
    .toolbar { margin-bottom: 16px; }
    .toolbar button { font-size: 12px; padding: 6px 14px; cursor: pointer; border: 1px solid #273480; background: #273480; color: #fff; border-radius: 4px; }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Save as PDF / Print</button>
  </div>
  <h1>Test Results Report</h1>
  <div class="sub">${cases.length} case(s) &middot; Generated ${esc(printedOn)}</div>
  ${body}
  <div class="footer">Printed: ${esc(printedOn)}</div>
</body>
</html>`;

      const win = window.open("", "_blank");
      if (!win) {
        ElMessage({ type: "error", message: "Please allow pop-ups to open the report.", duration: 3000 });
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
    },

    // Flatten a critical case's result rows into "Test: value (flag)" text
    criticalValuesText(row) {
      const results = Array.isArray(row.results) ? row.results : [];
      return results
        .map((r) => `${r.test_name}: ${r.result}${r.flag ? ` (${r.flag})` : ""}`)
        .join("; ");
    },

    formatCaseStatus(status) {
      if (!status) return "";
      if (status === "TEST_REPORTS") return "Test Reports";
      return status
        .split("_")
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(" ");
    },

    // Open a printable critical-tests report in a new tab (Save as PDF via print).
    // LabDaq-style layout: facility -> doctor -> patient row -> one row per critical
    // test with its "NOTE:" (test comment) underneath.
    // Exports every row matching the current filters (not just the visible page).
    async openCriticalReport() {
      if (!this.criticalTotal) return;
      // Open the tab before the request so the browser doesn't treat it as a pop-up
      const win = window.open("", "_blank");
      if (!win) {
        ElMessage({ type: "error", message: "Please allow pop-ups to open the report.", duration: 3000 });
        return;
      }
      win.document.write("<p style=\"font-family: Arial; padding: 24px\">Loading critical tests report…</p>");

      let rows = [];
      this.criticalExporting = true;
      try {
        const res = await this.fetchCriticalTests({
          ...this.criticalFilterParams(),
          page: 1,
          limit: this.criticalTotal,
        });
        rows = res.data || [];
      } catch {
        win.close();
        ElMessage({ type: "error", message: "Failed to load critical tests for export.", duration: 2000 });
        return;
      } finally {
        this.criticalExporting = false;
      }
      if (!rows.length) {
        win.close();
        return;
      }

      const facilities = {};
      const facilityOrder = [];
      rows.forEach((r) => {
        const fac = r.facility_name || "—";
        if (!facilities[fac]) {
          facilities[fac] = {};
          facilityOrder.push(fac);
        }
        const doc = r.ordering_physician || "—";
        if (!facilities[fac][doc]) facilities[fac][doc] = [];
        facilities[fac][doc].push(r);
      });

      const esc = (v) =>
        String(v ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      const fmtDate = (v, f) => (v && moment(v).isValid() ? moment(v).format(f) : "");
      const shortFlag = (flag) => {
        const f = String(flag || "").toUpperCase();
        if (f.includes("CRITICAL HIGH")) return "HH";
        if (f.includes("CRITICAL LOW")) return "LL";
        if (f === "HIGH") return "H";
        if (f === "LOW") return "L";
        if (f === "CRITICAL") return "C";
        return flag || "";
      };

      // Same 6-column grid for the header and every patient block so the columns line up:
      //   Accession | Patient | Birth  | Run Date/Time | (blank) | Phone
      //   (blank)   | Test    | Result | Units         | Flag    | Reference Range
      const colgroup = `<colgroup>
        <col style="width:11%" /><col style="width:25%" /><col style="width:12%" />
        <col style="width:20%" /><col style="width:10%" /><col style="width:22%" />
      </colgroup>`;

      let body = "";
      facilityOrder.forEach((fac) => {
        body += `<div class="facility">${esc(fac)}</div>`;
        Object.keys(facilities[fac]).forEach((doc) => {
          body += `<div class="doctor">Doctor: ${esc(doc === "—" ? "Not specified" : doc)}</div>`;
          facilities[fac][doc].forEach((r) => {
            const tests = r.critical_tests?.length
              ? r.critical_tests
              : (r.results || []).map((t) => ({ ...t, comment: "" }));
            const runDate = tests.find((t) => t.result_date)?.result_date || r.date;
            body += `<table class="rpt">${colgroup}<tbody>
              <tr class="pt-row">
                <td>${esc(r.accession_id || "")}</td>
                <td>${esc(r.name || "")}</td>
                <td>${esc(fmtDate(r.patient_dob, "M/D/YYYY"))}</td>
                <td>${esc(fmtDate(runDate, "M/D/YYYY h:mm A"))}</td>
                <td></td>
                <td>${esc(r.patient_phone || "")}</td>
              </tr>`;
            // Same test ordered in two panels (e.g. Sodium in BMP and Electrolyte Panel):
            // name the panel so the two results can be told apart
            const nameCounts = {};
            tests.forEach((t) => { nameCounts[t.test_name] = (nameCounts[t.test_name] || 0) + 1; });
            tests.forEach((t) => {
              const panel = nameCounts[t.test_name] > 1 && t.test_name_code
                ? ` <span class="panel-tag">(${esc(t.test_name_code)})</span>`
                : "";
              body += `<tr class="test-row">
                <td></td>
                <td class="c-test">${esc(t.test_name || "")}${panel}</td>
                <td>${esc(t.result ?? "")}</td>
                <td>${esc(t.units || "")}</td>
                <td>${esc(shortFlag(t.flag))}</td>
                <td>${esc(t.reference_range || "")}</td>
              </tr>`;
              if (t.comment) {
                body += `<tr class="note-row"><td></td><td colspan="5">NOTE: ${esc(t.comment)}</td></tr>`;
              }
            });
            if (r.comments) {
              body += `<tr class="note-row"><td></td><td colspan="5">REPORT COMMENT: ${esc(r.comments)}</td></tr>`;
            }
            body += `</tbody></table>`;
          });
        });
      });

      const printedOn = moment().format("M/D/YYYY h:mm A");
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Critical Tests Report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 24px; font-size: 11px; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .sub { color: #555; font-size: 11px; margin-bottom: 12px; }
    .col-head { width: 100%; table-layout: fixed; border-collapse: collapse; border-top: 2px solid #333; border-bottom: 1px solid #333; margin-bottom: 6px; }
    .col-head td { font-weight: 700; padding: 1px 6px; }
    .facility { font-weight: 700; font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase; border: 1px solid #333; padding: 3px 6px; margin-top: 10px; }
    .doctor { font-weight: 700; padding: 3px 0 2px; text-transform: uppercase; }
    table.rpt { width: 100%; border-collapse: collapse; table-layout: fixed; page-break-inside: avoid; }
    table.rpt td { padding: 2px 6px; vertical-align: top; overflow-wrap: anywhere; }
    .pt-row td { background: #e8e8e8; }
    .c-test { padding-left: 18px !important; }
    .panel-tag { color: #555; font-size: 10px; }
    .note-row td { font-weight: 700; padding-left: 36px; }
    .footer { margin-top: 20px; font-size: 10px; color: #666; border-top: 1px solid #ccc; padding-top: 6px; }
    @media print { body { margin: 12px; } .no-print { display: none; } }
    .toolbar { margin-bottom: 16px; }
    .toolbar button { font-size: 12px; padding: 6px 14px; cursor: pointer; border: 1px solid #273480; background: #273480; color: #fff; border-radius: 4px; }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Save as PDF / Print</button>
  </div>
  <h1>Critical Tests Report</h1>
  <div class="sub">${rows.length} case(s) &middot; Generated ${esc(printedOn)}</div>
  <table class="col-head">${colgroup}
    <tr><td>Accession</td><td>Patient</td><td>Birth</td><td>Run Date/Time</td><td></td><td>Phone</td></tr>
    <tr><td></td><td class="c-test">Test</td><td>Result</td><td>Units</td><td>Flag</td><td>Reference Range</td></tr>
  </table>
  ${body}
  <div class="footer">Printed: ${esc(printedOn)}</div>
</body>
</html>`;

      win.document.open();
      win.document.write(html);
      win.document.close();
    },

    // Open a printable report in a new tab (user can "Save as PDF" via the print dialog)
    openPendingReport() {
      const rows = this.filteredPending;
      if (!rows.length) return;

      // Group by ordering facility, then by ordering physician (mirrors the printout layout)
      const facilities = {};
      const facilityOrder = [];
      rows.forEach((r) => {
        const fac = r.hospital_name || "—";
        if (!facilities[fac]) {
          facilities[fac] = {};
          facilityOrder.push(fac);
        }
        const doc = r.ordering_physician || "—";
        if (!facilities[fac][doc]) facilities[fac][doc] = [];
        facilities[fac][doc].push(r);
      });

      const esc = (v) =>
        String(v ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      let body = "";
      facilityOrder.forEach((fac) => {
        body += `<div class="facility">${esc(fac)}</div>`;
        Object.keys(facilities[fac]).forEach((doc) => {
          body += `<div class="doctor">Doctor: ${esc(doc)}</div>`;
          body += `<table class="rpt"><thead><tr>
            <th class="c-acc">Accession</th>
            <th class="c-pat">Patient</th>
            <th class="c-dob">DOB</th>
            <th class="c-test">Pending Tests</th>
          </tr></thead><tbody>`;
          facilities[fac][doc].forEach((r) => {
            body += `<tr>
              <td class="c-acc">${esc(r.accession_id || "")}</td>
              <td class="c-pat">${esc(r.patient_name || "")}</td>
              <td class="c-dob">${r.patient_dob ? esc(this.formatDob(r.patient_dob)) : ""}</td>
              <td class="c-test">${esc((r.pending_tests || []).join(", "))}</td>
            </tr>`;
          });
          body += `</tbody></table>`;
        });
      });

      const printedOn = moment().format("MM/DD/YYYY HH:mm");
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Pending Tests Report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 24px; font-size: 12px; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .sub { color: #555; font-size: 11px; margin-bottom: 16px; }
    .facility { font-weight: 700; font-size: 12px; background: #f0f0f0; padding: 4px 6px; border-top: 1px solid #999; margin-top: 10px; }
    .doctor { font-style: italic; font-size: 11px; padding: 3px 6px; }
    table.rpt { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    table.rpt th { text-align: left; font-size: 10px; text-transform: uppercase; color: #333; border-bottom: 1px solid #333; padding: 2px 6px; }
    table.rpt td { padding: 2px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
    .c-acc { width: 15%; font-family: monospace; }
    .c-pat { width: 28%; }
    .c-dob { width: 12%; }
    .c-test { width: 45%; }
    .footer { margin-top: 20px; font-size: 10px; color: #666; border-top: 1px solid #ccc; padding-top: 6px; }
    @media print { body { margin: 12px; } .no-print { display: none; } }
    .toolbar { margin-bottom: 16px; }
    .toolbar button { font-size: 12px; padding: 6px 14px; cursor: pointer; border: 1px solid #273480; background: #273480; color: #fff; border-radius: 4px; }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Save as PDF / Print</button>
  </div>
  <h1>Pending Tests Report</h1>
  <div class="sub">${rows.length} case(s) &middot; Generated ${esc(printedOn)}</div>
  ${body}
  <div class="footer">Printed: ${esc(printedOn)}</div>
</body>
</html>`;

      const win = window.open("", "_blank");
      if (!win) {
        ElMessage({ type: "error", message: "Please allow pop-ups to open the report.", duration: 3000 });
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
    },

    // Open (or focus) an instrument's workspace tab from its tile
    async openResults(category, inst) {
      if (!inst.instrument_id) return;
      this.rememberTabFilters();
      let tab = this.openTabs.find((t) => t.instrument_id === inst.instrument_id);
      if (!tab) {
        tab = { instrument_id: inst.instrument_id, name: inst.name };
        this.openTabs.push(tab);
      }
      tab.resultsFilter = category === "ALL" ? "" : category;
      tab.reviewFilter = "PENDING";
      await this.activateTab(tab);
    },

    async switchInstrumentTab(tab) {
      if (tab.instrument_id === this.resultsTab.instrumentId) return;
      this.rememberTabFilters();
      await this.activateTab(tab);
    },

    async activateTab(tab) {
      this.resultsTab = { instrumentId: tab.instrument_id, name: tab.name };
      this.resultsFilter = tab.resultsFilter || "";
      this.reviewFilter = tab.reviewFilter ?? "PENDING";
      await this.loadResults();
    },

    rememberTabFilters() {
      const current = this.openTabs.find(
        (t) => t.instrument_id === this.resultsTab.instrumentId
      );
      if (!current) return;
      current.resultsFilter = this.resultsFilter;
      current.reviewFilter = this.reviewFilter;
    },

    async closeInstrumentTab(tab) {
      const idx = this.openTabs.indexOf(tab);
      if (idx === -1) return;
      const wasActive = tab.instrument_id === this.resultsTab.instrumentId;
      this.openTabs.splice(idx, 1);
      if (!wasActive) return;
      // Like a browser: focus the neighbouring tab, or fall back to the overview
      const next = this.openTabs[idx] || this.openTabs[idx - 1];
      if (next) await this.activateTab(next);
      else this.showOverview();
    },

    // Back to the instrument tiles; refresh counts since results may have been accepted
    showOverview() {
      if (!this.resultsTab.instrumentId) return;
      this.rememberTabFilters();
      this.clearResults();
      this.resultsTab = { instrumentId: null, name: "" };
      this.loadDashboard();
    },

    clearResults() {
      this.stopProgressiveRender();
      this.results = [];
      this.caseComments = {};
      this.expandedTrends = {};
    },

    toggleTrend(rowId) {
      if (this.expandedTrends[rowId]) delete this.expandedTrends[rowId];
      else this.expandedTrends[rowId] = true;
    },

    trendToneClass(flag) {
      if (!flag) return "tone-normal";
      const f = flag.toUpperCase();
      if (this.isCriticalFlag(flag)) return "tone-critical";
      if (f === "HIGH" || f === "H" || f === "LOW" || f === "L" || f === "ABNORMAL") return "tone-abnormal";
      return "tone-normal";
    },

    async acceptCase(caseDocId) {
      const missing = this.results.filter(
        (r) => r._caseDocId === caseDocId && this.rowNeedsComment(r)
      );
      if (missing.length) {
        ElMessage({
          type: "warning",
          message: `Add a comment for the critical result(s) before accepting this case: ${missing
            .map((r) => r.test_name)
            .join(", ")}.`,
          duration: 3500,
        });
        return;
      }
      if (this.caseAccepting[caseDocId]) return;
      this.caseAccepting[caseDocId] = true;
      try {
        await this.reviewCase({ caseDocId });
        // Mirror the API: pending rows become accepted, held rows stay held
        this.results.forEach((r) => {
          if (r._caseDocId === caseDocId && r.review_status !== "HELD") r.review_status = "ACCEPTED";
        });
        ElMessage({ type: "success", message: "Case accepted.", duration: 2000 });
      } catch {
        ElMessage({ type: "error", message: "Failed to accept case.", duration: 2000 });
      } finally {
        this.caseAccepting[caseDocId] = false;
      }
    },

    async loadResults() {
      if (!this.resultsTab.instrumentId) return;
      this.resultsLoading = true;
      this.clearResults();
      try {
        const params = {};
        if (this.resultsFilter) params.result_category = this.resultsFilter;
        if (this.reviewFilter) params.review_status = this.reviewFilter;
        await this.fetchInstrumentResults({
          instrumentId: this.resultsTab.instrumentId,
          params,
        });
        const data = this.getBatchResults;
        const caseDocs = data.data || [];
        const rows = [];
        const comments = {};
        const previousMap = {};
        caseDocs.forEach((caseDoc) => {
          comments[caseDoc._id] = caseDoc.report_comment || "";
          previousMap[caseDoc._id] = caseDoc.previous_results || [];
          (caseDoc.results || []).forEach((res) => {
            rows.push({
              ...res,
              _caseDocId: caseDoc._id,
              accession_id: caseDoc.accession_id,
              patient_first_name: caseDoc.patient_first_name,
              patient_middle_name: caseDoc.patient_middle_name,
              patient_last_name: caseDoc.patient_last_name,
              patient_gender: caseDoc.patient_gender,
              patient_dob: caseDoc.patient_dob,
              collection_date: res.collection_date || caseDoc.collection_date || null,
              comment: res.comment || "",
              _lastSavedComment: res.comment || "",
              _commentSaving: false,
              _commentSaved: false,
              _pendingAction: res.review_status === "HELD" ? "HOLD" : "ACCEPT",
              _applying: false,
            });
          });
        });
        this.results = rows;
        this.caseComments = comments;
        this.casePreviousResults = previousMap;
        this.startProgressiveRender();
      } finally {
        this.resultsLoading = false;
      }
    },

    // Show the first cases straight away, then add the rest in small batches between
    // frames until every case is on the page
    startProgressiveRender() {
      const BATCH = 15;
      this.stopProgressiveRender();
      this.renderedCaseCount = BATCH;
      const step = () => {
        if (this.renderedCaseCount >= this.groupedResults.length) {
          this.renderTimer = null;
          return;
        }
        this.renderedCaseCount += BATCH;
        this.renderTimer = setTimeout(step, 30);
      };
      this.renderTimer = setTimeout(step, 30);
    },

    stopProgressiveRender() {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = null;
    },

    async setCategory(val) {
      this.resultsFilter = val;
      await this.loadResults();
    },

    async setStatus(val) {
      this.reviewFilter = val;
      await this.loadResults();
    },

    async acceptAll() {
      if (!this.resultsTab.instrumentId) return;
      const missing = this.results.filter((r) => this.rowNeedsComment(r));
      if (missing.length) {
        ElMessage({
          type: "warning",
          message: `Add a comment for all critical results before accepting: ${missing
            .map((r) => `${r.accession_id} – ${r.test_name}`)
            .join(", ")}.`,
          duration: 4000,
        });
        return;
      }
      const category = this.resultsFilter || "ALL";
      this.acceptAllLoading = true;
      try {
        const res = await this.bulkReviewInstrument({
          instrumentId: this.resultsTab.instrumentId,
          resultCategory: category,
        });
        ElMessage({ type: "success", message: `${res.accepted_count ?? 'All'} results accepted.`, duration: 2500 });
        await this.loadResults();
      } catch {
        ElMessage({ type: "error", message: "Accept All failed.", duration: 2000 });
      } finally {
        this.acceptAllLoading = false;
      }
    },

    onActionChange(val, row) {
      if (val === "HOLD") {
        this.confirmHold(row);
      } else {
        this.applyAction(val, row);
      }
    },

    // Puts the dropdown back on the row's actual status after a blocked/failed action
    resetPendingAction(row) {
      row._pendingAction = row.review_status === "HELD" ? "HOLD" : "ACCEPT";
    },

    async confirmHold(row) {
      row._applying = true;
      try {
        await this.reviewSingleResult({ caseDocId: row._caseDocId, resultId: row._id, action: "HELD" });
        row.review_status = "HELD";
        ElMessage({ type: "warning", message: "Result placed on hold.", duration: 2000 });
      } catch {
        this.resetPendingAction(row);
        ElMessage({ type: "error", message: "Hold failed. Please try again.", duration: 2000 });
      } finally {
        row._applying = false;
      }
    },

    async applyAction(action, row) {
      if (action === "ACCEPT" && this.missingCriticalComment(row)) {
        this.resetPendingAction(row);
        ElMessage({
          type: "warning",
          message: `A comment is required for the critical result "${row.test_name}" before it can be accepted.`,
          duration: 3000,
        });
        return;
      }
      row._applying = true;
      try {
        if (action === "ACCEPT") {
          await this.reviewSingleResult({ caseDocId: row._caseDocId, resultId: row._id, action: "ACCEPTED" });
          row.review_status = "ACCEPTED";
          ElMessage({ type: "success", message: "Result accepted.", duration: 2000 });
        }
      } catch {
        this.resetPendingAction(row);
        ElMessage({ type: "error", message: "Action failed. Please try again.", duration: 2000 });
      } finally {
        row._applying = false;
      }
    },

    async saveComment(caseDocId) {
      this.caseSaving[caseDocId] = true;
      try {
        await this.updateCaseComment({ caseDocId, comment: this.caseComments[caseDocId] || "" });
        ElMessage({ type: "success", message: "Comment saved.", duration: 2000 });
      } catch {
        ElMessage({ type: "error", message: "Failed to save comment.", duration: 2000 });
      } finally {
        this.caseSaving[caseDocId] = false;
      }
    },

    // Preset comments only apply to critical / abnormal results
    isCommentPresetFlag(flag) {
      if (!flag) return false;
      const f = flag.toUpperCase();
      return f === "HH" || f === "LL" || f.includes("CRITICAL");
    },

    // Critical flags (high or low) — these require a comment before accepting
    isCriticalFlag(flag) {
      if (!flag) return false;
      const f = flag.toUpperCase();
      return f === "HH" || f === "LL" || f.includes("CRITICAL");
    },

    // A critical result can't be accepted without a comment
    missingCriticalComment(row) {
      return (
        this.isCriticalFlag(row && row.flag) &&
        !((row.comment || "").trim())
      );
    },


    rowNeedsComment(row) {
      return (
        !!row &&
        row.review_status !== "HELD" &&
        row.review_status !== "ACCEPTED" &&
        this.missingCriticalComment(row)
      );
    },

    queryCommentSuggestions(queryString, cb, row) {
      if (!this.isCommentPresetFlag(row && row.flag)) {
        cb([]);
        return;
      }
      const q = (queryString || "").toLowerCase();
      const matches = this.commentPresets
        .filter((c) => !q || c.toLowerCase().includes(q))
        .map((c) => ({ value: c }));
      cb(matches);
    },

    async saveResultComment(group, row) {
      const comment = row.comment ?? "";
      if (comment === (row._lastSavedComment ?? "")) return;
      row._commentSaving = true;
      row._commentSaved = false;
      try {
        await this.updateResultComment({
          caseDocId: group._caseDocId,
          resultId: row._id,
          comment,
        });
        row._lastSavedComment = comment;
        row._commentSaved = true;
        setTimeout(() => {
          row._commentSaved = false;
        }, 2000);
      } catch {
        ElMessage({ type: "error", message: "Failed to save comment.", duration: 2000 });
      } finally {
        row._commentSaving = false;
      }
    },

    patientName(row) {
      return [row.patient_first_name, row.patient_middle_name, row.patient_last_name]
        .filter(Boolean)
        .join(" ") || "—";
    },

    flagShortLabel(flag) {
      if (!flag) return "";
      const f = flag.toUpperCase();
      if (f === "HH" || f.includes("CRITICAL HIGH")) return "CH";
      if (f === "LL" || f.includes("CRITICAL LOW")) return "CL";
      if (f === "HIGH" || f === "H") return "H";
      if (f === "LOW" || f === "L") return "L";
      if (f === "ABNORMAL") return "ABNORMAL";
      if (f === "NORMAL") return "";
      return flag;
    },

    flagPillClass(flag) {
      if (!flag) return "";
      const f = flag.toUpperCase();
      if (f === "HH" || f.includes("CRITICAL HIGH")) return "pill-critical-high";
      if (f === "LL" || f.includes("CRITICAL LOW")) return "pill-critical-low";
      if (f === "HIGH" || f === "H") return "pill-high";
      if (f === "LOW" || f === "L") return "pill-low";
      if (f === "ABNORMAL") return "pill-abnormal";
      return "";
    },

    flagRowClass(flag) {
      if (!flag) return "";
      const f = flag.toUpperCase();
      if (f === "HH" || f === "CRITICAL HIGH" || f === "CRITICAL_HIGH") return "row-critical-high";
      if (f === "LL" || f === "CRITICAL LOW" || f === "CRITICAL_LOW") return "row-critical-low";
      if (f === "HIGH" || f === "H") return "row-high";
      if (f === "LOW" || f === "L") return "row-low";
      if (f === "ABNORMAL") return "row-abnormal";
      return "";
    },

    formatDate(d) {
      return moment(d).format("MM/DD/YYYY");
    },
    // DOB is stored as UTC midnight; format in UTC so it doesn't shift a day.
    formatDob(d) {
      return moment.utc(d).format("MM/DD/YYYY");
    },
    formatShortDate(d) {
      return d ? moment(d).format("MM/DD/YYYY") : "";
    },
    // Sparkline path inside a 64x22 box (3px inset so the end dot isn't clipped)
    sparkGeometry(points) {
      const W = 64, H = 22, PAD = 3;
      const values = points.map((p) => p.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = max - min || 1;
      const step = (W - PAD * 2) / (points.length - 1);
      const coords = values.map((v, i) => [
        PAD + i * step,
        max === min ? H / 2 : PAD + (1 - (v - min) / span) * (H - PAD * 2),
      ]);
      const [lastX, lastY] = coords[coords.length - 1];
      return {
        path: coords.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" "),
        lastX,
        lastY,
      };
    },

    // Today's result vs the previous numeric run; changes under 5% read as flat
    trendDirection(points) {
      const cur = points[points.length - 1].value;
      const prev = points[points.length - 2].value;
      const base = Math.abs(prev) || 1;
      const change = (cur - prev) / base;
      if (change > 0.05) return "up";
      if (change < -0.05) return "down";
      return "flat";
    },

    prevCellValue(group, row, colKey) {
      const code = row.result_code || row.test_name;
      const byCol = group.prevLookup?.[code];
      const val = byCol ? byCol[colKey] : undefined;
      return val === undefined || val === null || val === "" ? "—" : val;
    },
  },
};
</script>

<style lang="scss" scoped>
#instrument-queue-view {
  display: flex;
  align-items: stretch;
  background: #f0f3f8;
  min-height: calc(100vh - 70px);

  /* ── Lab Console sidebar ── */
  .iq-sidebar {
    flex: 0 0 220px;
    width: 220px;
    background: #fff;
    border-right: 1px solid #e3e8ef;
    display: flex;
    flex-direction: column;

    &__title {
      padding: 20px 18px 14px;
      font-size: 17px;
      font-weight: 700;
      color: #273480;
      letter-spacing: 0.3px;
      border-bottom: 1px solid #e3e8ef;
    }

    &__nav {
      padding: 16px 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
  }

  .iq-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 42px;
    padding: 11px 14px;
    border-radius: 10px;
    color: #4a5568;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: currentColor;
      flex-shrink: 0;
    }

    &:hover {
      background: #eef2f7;
      color: #273480;
    }

    &.active {
      background: #BF1B39;
      color: #fff;
    }

    &.disabled {
      color: #b6bfcc;
      cursor: default;
      pointer-events: none;
      background: #f4f6f9;
    }
  }

  /* ── Main content ── */
  .iq-main {
    flex: 1;
    min-width: 0;
    padding: 24px 30px;
  }

  .iq-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    h2 {
      font-size: 15px;
      font-weight: 700;
      color: #273480;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .iq-page-header__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* ── Workspace tabs (overview + one per opened instrument) ── */
  .iq-tabs {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    margin: -8px 0 20px;
    border-bottom: 1px solid #dde4f0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .iq-tab {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 240px;
    padding: 9px 14px;
    border: 1px solid transparent;
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: #e6ebf3; color: #273480; }

    &.active {
      background: #fff;
      color: #273480;
      font-weight: 600;
      border-color: #dde4f0;
      margin-bottom: -1px; /* sit on top of the strip's bottom border */
      padding-bottom: 10px;
      box-shadow: inset 0 2px 0 #BF1B39;
    }

    &__name { overflow: hidden; text-overflow: ellipsis; }

    &__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      font-size: 15px;
      line-height: 1;
      color: #94a3b8;
      flex-shrink: 0;

      &:hover { background: #dbe2ee; color: #1e293b; }
    }
  }

  /* ── Results view (fills the page instead of a modal) ── */
  .iq-results-view {
    background: #fff;
    border: 1px solid #dde4f0;
    border-radius: 8px;
    padding: 16px 20px 12px;
    animation: filterFadeIn 0.2s ease both;
  }

  .iq-page-header--results { margin-bottom: 14px; }

  .iq-results-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;

    h2 { margin: 0; text-transform: none; font-size: 17px; letter-spacing: 0.2px; }
  }

  .iq-crumb {
    font-size: 13px;
    color: #64748b;
    cursor: pointer;

    &:hover { color: #273480; text-decoration: underline; }
  }

  .iq-crumb-sep { color: #cbd5e1; }

  .iq-results-count {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 4px;
  }

  .iq-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
  }

  .iq-tile {
    background: #2b3d72;
    border-radius: 6px;
    padding: 14px 16px 12px;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: transform 0.18s ease, box-shadow 0.18s ease;

    &:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(43, 61, 114, 0.3); }

    &__top { display: flex; align-items: flex-start; }

    &__name {
      font-size: 13px;
      font-weight: 700;
      line-height: 1.4;
    }

    &__counts { display: flex; gap: 18px; }

    &__count {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.65);

      strong { margin-left: 4px; font-weight: 700; }

      &.highlight { color: #f6c90e; strong { color: #f6c90e; } }
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 2px;
    }

    &__date {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
    }

    &__action-btn {
      font-size: 11px;
      font-weight: 600;
      color: #2b3d72;
      cursor: pointer;
      padding: 4px 12px;
      border-radius: 4px;
      background: #fff;
      transition: background 0.15s, transform 0.1s;
      white-space: nowrap;

      &:hover { background: #e8edf8; }
      &:active { transform: scale(0.95); }
    }
  }

  .iq-empty {
    text-align: center;
    padding: 50px;
    color: #999;
    font-size: 14px;
  }

  .iq-loading { padding: 20px 0; }

  /* ── Pending Tests tab ── */
  .pt-filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #dde4f0;
    border-radius: 6px;
  }

  .pt-filter { width: 220px; }
  .pt-filter--date { width: 260px; }
}
</style>

<style lang="scss">
/* ── Prefilled comment suggestions ── */
.result-comment-suggestions.el-popper {
  max-width: 560px;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(39, 52, 128, 0.14);

  .el-autocomplete-suggestion__wrap {
    padding: 0;
    max-height: 320px;
  }

  li {
    padding: 0;
    line-height: normal;
    border-radius: 8px;
    margin-bottom: 2px;

    &:last-child { margin-bottom: 0; }

    .preset-item {
      display: flex;
      align-items: flex-start;
      gap: 9px;
      padding: 9px 11px;

      &__icon {
        flex-shrink: 0;
        margin-top: 1px;
        color: #94a3b8;
        transition: color 0.15s ease;
      }

      &__text {
        font-size: 12.5px;
        line-height: 1.45;
        color: #475569;
        white-space: normal;
        word-break: break-word;
      }
    }

    &.highlighted,
    &:hover {
      background: #f1f5f9;
      .preset-item__icon {
          color: #BF1B39;
        }
      .preset-item__text { color: #1f2937; }
    }
  }
}

/* ── Tile dropdown animation ── */
.iq-tile-dropdown {
  .el-dropdown-menu__item {
    font-size: 13px;
    transition: background 0.15s ease, padding-left 0.15s ease;

    &:not(.is-disabled):hover {
      padding-left: 22px;
      background: #ecf0fc;
      color: #273480;
    }
  }
}

/* ── Filter bar ── */
.iq-dialog-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #dde4f0;
  border-radius: 6px;
  flex-wrap: wrap;
  animation: filterFadeIn 0.25s ease both;

  .filter-left {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .btn-accept-all {
    padding: 6px 18px;
    font-size: 12px;
    font-weight: 600;
    background: #273480;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, transform 0.1s;

    &:hover:not(:disabled) { background: #1e2870; }
    &:active:not(:disabled) { transform: scale(0.96); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  .filter-group {
    display: flex;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;

    .filter-btn {
      padding: 5px 16px;
      font-size: 12px;
      background: #fff;
      border: none;
      cursor: pointer;
      color: #606266;
      position: relative;
      transition: background 0.18s ease, color 0.18s ease, transform 0.1s ease;

      &:not(:last-child) { border-right: 1px solid #dcdfe6; }

      &:active { transform: scale(0.92); }

      &.active {
        background: #273480;
        color: #fff;
        box-shadow: inset 0 -2px 0 rgba(255, 255, 255, 0.2);
      }

      &:hover:not(.active) { background: #ecf0fc; color: #273480; }
    }
  }
}

/* ── Animations ── */
@keyframes tableReveal {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes filterFadeIn {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Shared column widths (used by header + every data row) ── */
.col        { padding: 0 12px; box-sizing: border-box; }
.col-name   { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-left: 0; }
.col-prev   { width: 110px; flex-shrink: 0; text-align: center; }
.col-prevdate { width: 100px; flex-shrink: 0; text-align: center; color: #64748b; }
.col-result { width: 100px; flex-shrink: 0; text-align: center; }
.col-range  { width: 130px; flex-shrink: 0; text-align: center; }
.col-flag   { width: 76px;  flex-shrink: 0; text-align: center; }
.col-action { width: 130px; flex-shrink: 0; text-align: center; }

/* ── Shared column widths ── */
.col        { padding: 0 10px; box-sizing: border-box; flex-shrink: 0; }
.col-name   { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-left: 0; }
.col-prev   { width: 90px;  text-align: right; }
.col-prevdate { width: 90px; text-align: center; color: #64748b; }
.col-result { width: 90px;  text-align: right; }
.col-range  { width: 120px; text-align: center; }
.col-flag   { width: 110px; text-align: center; padding: 0 4px; } /* fits "NONREACTIVE" */
.col-trend  { width: 96px;  text-align: center; }

/* History column header: date on top, prior accession beneath */
.rpt-col-header .col-prevdate {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.prevdate-date { font-size: 10px; }
.prevdate-acc { font-size: 8px; font-weight: 600; color: #94a3b8; letter-spacing: 0; }
.col-action { width: 130px; text-align: center; }

/* ════════════════════════════════
   REPORT STYLE
════════════════════════════════ */
.report-list {
  overflow-y: auto;
  max-height: 60vh;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

/* ── Report list ── */
.report-list {
  overflow-y: auto;
  max-height: 62vh;
  padding: 0 2px 8px;
}

/* ── One case section ── */
.report-case {
  padding-top: 0;
}

/* ── Results dialog: wide history tables scroll horizontally ──
   Each case grows to its full column width so row backgrounds and the rule
   span every column. Test Name stays pinned left, Result → Action pinned
   right, and the header / comment rows stay in view while scrolling. */
.report-list--results {
  /* Sticky full-width rows span the visible list, not the scrolled content */
  container-type: inline-size;
  --iq-view-w: 100cqw;
  overflow: auto;
  max-height: max(360px, calc(100vh - 300px));
  padding-right: 0; /* else the scrolled columns peek out past the pinned Action column */

  .report-case {
    width: max-content;
    min-width: 100%;
  }

  .report-case__header,
  .report-panel__name,
  .result-trend,
  .result-comment,
  .case-report-comment {
    position: sticky;
    left: 0;
    width: var(--iq-view-w);
    max-width: 100%;
    box-sizing: border-box;
  }

  .result-row { background: #fff; }

  /* Pinned cells fill the full row height so nothing scrolls visibly
     behind them; they centre their content with flex instead. */
  .col-name,
  .col-result,
  .col-range,
  .col-flag,
  .col-trend,
  .col-action {
    position: sticky;
    z-index: 2;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    background: inherit;
  }
  .col-name {
    flex: 1 0 180px;
    left: 0;
    display: block; /* block (not flex) keeps the ellipsis working */
    line-height: 34px;
    box-shadow: 1px 0 0 #e8edf5;
  }
  .rpt-col-header .col-name { line-height: 28px; }
  .result-row .col-result { justify-content: flex-end; }
  .rpt-col-header .col-result { justify-content: flex-end; }
  .col-action { right: 0; }
  .col-flag   { right: 130px; }
  .col-range  { right: 240px; }
  .col-result { right: 360px; box-shadow: -1px 0 0 #e8edf5; }
  .col-trend  { right: 450px; box-shadow: -1px 0 0 #e8edf5; }

  .rpt-col-header .col { background: #fff; }
  .report-panel__name { background: #fff; }
}

/* ── Sticky wrapper: patient header + rule + col labels ── */
.report-case__sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding-top: 18px;
}

/* ── Patient header line ── */
.report-case__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 6px;
}

.report-case__patient {
  display: flex;
  align-items: baseline;
  gap: 0;
  flex-wrap: wrap;
  min-width: 0;
}

.rpt-label    { font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 1.2px; text-transform: uppercase; flex-shrink: 0; }
.rpt-dot      { margin: 0 7px; color: #cbd5e1; flex-shrink: 0; }
.rpt-sep      { margin: 0 10px; flex-shrink: 0; }
.rpt-accession { font-family: monospace; font-size: 11px; color: #64748b; letter-spacing: 0.4px; flex-shrink: 0; }
.rpt-name     { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: 0.2px; }
.rpt-meta     { font-size: 12px; color: #64748b; flex-shrink: 0; }

.rpt-accept-btn {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 16px;
  border-radius: 5px;
  border: 1.5px solid #273480;
  background: transparent;
  color: #273480;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s, transform 0.1s;

  &:hover:not(:disabled) { background: #273480; color: #fff; }
  &:active:not(:disabled) { transform: scale(0.95); }
  &:disabled { opacity: 0.4; cursor: not-allowed; border-color: #94a3b8; color: #94a3b8; }
}

/* ── Thick rule below patient header ── */
.report-case__rule {
  height: 2px;
  background: #1e293b;
  border-radius: 1px;
  margin-bottom: 0;
}

/* ── Column label row (per case, inside the rule) ── */
.rpt-col-header {
  display: flex;
  align-items: center;
  height: 28px;
  border-bottom: 1px solid #e8edf5;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: transparent;
}

/* ── Panel sub-label (just text, no styled row) ── */
.report-panel__name {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 9px 0 4px;
  border-bottom: 1px dashed #e2e8f0;
}

/* ── Test rows ── */
.result-row {
  display: flex;
  align-items: center;
  min-height: 34px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  color: #1e293b;

  .cell-num { font-variant-numeric: tabular-nums; }

  &:last-child { border-bottom: none; }
  &:hover { background: #f8faff !important; }

  &.row-critical-high { background: #fee2e2 !important; color: #b91c1c; &:hover { background: #fecaca !important; } }
  &.row-high          { background: #fff4f4 !important; &:hover { background: #fee2e2 !important; } }
  &.row-critical-low  { background: #dbeafe !important; color: #1d4ed8; &:hover { background: #bfdbfe !important; } }
  &.row-low           { background: #eff6ff !important; &:hover { background: #dbeafe !important; } }
  &.row-abnormal      { background: #fffbeb !important; &:hover { background: #fef3c7 !important; } }
}

/* ── Per-test comment line ── */
.result-item {
  border-bottom: 1px solid #f1f5f9;

  &:last-child { border-bottom: none; }

  .result-row { border-bottom: none; }
}

.result-comment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 6px 12px;
}

.result-comment-input {
  flex: 1;
  min-width: 0;
  width: 100%;

  :deep(.el-input__inner) {
    height: 28px;
    line-height: 28px;
    font-size: 12px;
    color: #475569;
    padding: 3px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    background: #f8fafc;
    box-shadow: none;
    transition: border-color 0.15s, background 0.15s;

    &::placeholder { color: #cbd5e1; }
    /* Keep the border steady on hover — Element Plus' default hover darkens it
       to gray, which on this full-width input reads as a stray horizontal line. */
    &:hover { border-color: #e2e8f0; }
    &:focus,
    &:hover:focus { border-color: #273480; background: #fff; }
  }

  &.is-disabled :deep(.el-input__inner) {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.result-comment-status {
  font-size: 11px;
  color: #94a3b8;
  flex-shrink: 0;

  &.saved { color: #22c55e; }
  &.required { color: #dc2626; font-weight: 600; }
}

/* ── Case-level report comment ── */
.case-report-comment {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0 6px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
}
.report-comment-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.report-comment-input {
  flex: 1;
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid #dde4f0;
  border-radius: 4px;
  outline: none;
  color: #1e293b;
  background: #f8faff;
  transition: border-color 0.15s, background 0.15s;
  &:focus { border-color: #273480; background: #fff; }
  &::placeholder { color: #94a3b8; }
}
.report-comment-save {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 14px;
  background: #273480;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, transform 0.1s;
  &:hover:not(:disabled) { background: #1e2870; }
  &:active:not(:disabled) { transform: scale(0.95); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

/* ── Gap between cases ── */
.report-case__gap {
  margin-top: 18px;
  border-top: 1px solid #e2e8f0;
}

/* ── Flag pills ── */
.cell-muted { color: #cbd5e1; }
.flag-pill {
  display: inline-block; padding: 2px 7px; border-radius: 10px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  background: #f1f5f9; color: #475569;

  &.pill-critical-high { background: #b91c1c; color: #fff; }
  &.pill-high          { background: #fee2e2; color: #b91c1c; border: 1px solid #fca5a5; }
  &.pill-critical-low  { background: #1d4ed8; color: #fff; }
  &.pill-low           { background: #dbeafe; color: #1d4ed8; border: 1px solid #93c5fd; }
  &.pill-abnormal      { background: #fef3c7; color: #d97706; border: 1px solid #fcd34d; }
}

/* ── Trend sparkline cell ── */
.trend-spark {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover, &.open { background: #fff; border-color: #c7d2e6; }

  &__line { fill: none; stroke: #273480; stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; opacity: 0.75; }

  &__now {
    fill: #273480; stroke: #fff; stroke-width: 1;
    &.tone-critical { fill: #b91c1c; }
    &.tone-abnormal { fill: #d97706; }
  }

  &__dir {
    font-size: 9px;
    width: 9px;
    color: #94a3b8;
    &.is-up, &.is-down { color: #475569; }
  }
}

.result-trend { padding: 0 0 4px; }

/* ── Action elements ── */
.action-select {
  width: 95px !important;
  .el-input__wrapper { padding: 0 6px; }
  .el-input__inner { font-size: 12px; height: 26px; line-height: 26px; font-weight: 500; }
  .el-select__caret { font-size: 11px; }
}
.test-comment-line { line-height: 1.4; }
.btn-spinner {
  display: inline-block; width: 11px; height: 11px; margin-right: 5px;
  border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%;
  vertical-align: -1px; animation: btn-spin 0.7s linear infinite;
}
@keyframes btn-spin { to { transform: rotate(360deg); } }
.row-applying { font-size: 12px; font-weight: 500; color: #64748b; white-space: nowrap; }
.action-select-native {
  width: 95px; height: 26px; padding: 0 6px; font-size: 12px; font-weight: 500;
  color: #374151; background: #fff; border: 1px solid #dcdfe6; border-radius: 4px; cursor: pointer;
  &:focus { outline: none; border-color: #273480; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &.is-held { background: #fef3c7; border-color: #f59e0b; color: #b45309; font-weight: 600; }
}
.iq-rendering-more { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 12px; font-size: 12px; color: #64748b; }
.done-label { font-size: 13px; font-weight: 600; color: #22c55e; }

/* ── Search by Test tab ── */
.search-results {
  min-height: 200px;
  margin-bottom: 12px;

  /* Drop the shared dashed panel separator here — it reads as a stray
     full-width gray line between panels. */
  .report-panel__name {
    border-bottom: none;
    padding-bottom: 2px;
  }
}

.search-test-table {
  width: 100%;
  border-collapse: collapse;
  margin: 2px 0 6px;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #64748b;
    padding: 4px 8px;
    border-bottom: 1px solid #e2e8f0;
  }
  td {
    padding: 5px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: top;
  }
  tbody tr:last-child td { border-bottom: none; }

  .stt-name  { width: 28%; font-weight: 500; }
  .stt-result{ width: 16%; font-variant-numeric: tabular-nums; }
  .stt-units { width: 14%; color: #64748b; }
  .stt-range { width: 30%; color: #64748b; }
  .stt-flag  { width: 12%; }

  tr.row-critical-high td { background: #fee2e2; color: #b91c1c; }
  tr.row-high          td { background: #fff4f4; }
  tr.row-critical-low  td { background: #dbeafe; color: #1d4ed8; }
  tr.row-low           td { background: #eff6ff; }
  tr.row-abnormal      td { background: #fffbeb; }
}
</style>
